<template>
  <a class="car-card" :style="{ '--image-aspect': ar }" :class="{ busy: busy }" :href="vehicleURL" target="_blank" @click.stop.prevent="gotoVehicle">
    <!-- Photos -->
    <div class="skeleton">
      <div class="media-scroller no-scrollbar">
        <img v-for="(p, i) in photos" :key="i" loading="lazy" :src="$api.replaceLocalhost(p)" :alt="name">
      </div>
      <button class="slide-btn" data-dir="next" aria-label="next-image" @click.stop.prevent="handleSlide">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </button>
      <button class="slide-btn" data-dir="prev" aria-label="previous-image" @click.stop.prevent="handleSlide">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
      </button>
    </div>
    <!-- Badge-->
    <p class="car-badge" v-if="!busy && badge" :style="{ 'background-color': badge.color }">
      {{ badge.text }}
    </p>
    <!-- Details -->
    <div class="content">
      <div class="meta skeleton">
        <div class="tags">
          <span>{{ fuel }}</span>
          <span>{{ year }}</span>
        </div>
        <div class="action" v-if="favorite_action">
          <client-only>
            <div @click.stop.prevent="toggleFavourite" :class="{ 'user-fav': userFavourite }">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1"
                stroke="var(--crf-border)" style="width:20px;height:20px">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
          </client-only>
        </div>
      </div>
      <p class="name skeleton" :title="name">{{ name }}</p>
      <p v-if="!package.includes('try_and_buy')" class="foot skeleton">
        <sup v-if="strike" class="strike">CHF {{ strike }}</sup>
        <sup v-else>from</sup>
        <span class="price">{{ price }}</span>
        <span class="period"> /<translate>month</translate></span>
      </p>
      <p v-else class="foot skeleton">
          <span class="small-period"> <translate>Try @</translate></span>
          <span class="small-monthly-price"> {{ price }} </span>
          <span class="small-period"> /<translate>month</translate></span>
      </p>
      <p v-if="km_option && km_option.try_and_buy && package.includes('try_and_buy')" class="foot skeleton">
        <span class="listing-price">
          {{renderLargePrice(km_option.try_and_buy.end_price)}}
        </span>
      </p>
    </div>
  </a>
</template>

<script>
export default {
  name: "cms-vehicle-card",
  props: {
    ar: {
      type: String,
      default: "4/3"
    },
    id: {
      type: String,
      required: true,
    },
    slug: {
      type: String,
    },
    name: {
      type: String,
      required: true
    },
    brand: {
      type: String,
      required: true,
    },
    thumb: {
      type: String,
      required: false,
    },
    badge: {
      type: Object,
      required: false,
    },
    images: {
      type: Array,
      default: () => []
    },
    price: {
      type: Number,
      required: true,
      default: NaN
    },
    km_option: {
      type: Object,
      required: false,
      default() {
        return {}
      }
    },
    strike: {
      type: String,
    },
    busy: {
      type: Boolean,
      default: false
    },
    favourite: {
      type: Boolean,
      default: false,
    },
    fuel: {
      type: String,
      default: "???"
    },
    year: {
      type: Number,
      default: NaN
    },
    distance: {
      type: Number,
    },
    duration: {
      type: Number,
    },
    package: {
      type: Array
    },
    favorite_action: {
      type: Boolean,
      default: true
    },
    down_payment_adjustment_id: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      userFavourite: this.favourite
    }
  },
  computed: {
    vehicleURL() {
      const query = new URLSearchParams();
      query.append("from_search", true)
      if (this.package.length > 0) {
        query.append("package_type", this.package.join(","))
      }
      if (this.duration) {
        query.append("minimum_duration", this.duration)
      }
      if (this.distance) {
        query.append("km", this.distance)
      }
      if (this.down_payment_adjustment_id) {
        query.append("down_payment_adjustment_id", this.down_payment_adjustment_id)
      }
      return `${this.$config.BASE_URL}${this.$language.current}/view/${this.slug}/${this.id}?${query.toString()}`
    },
    photos() {
      return [this.thumb, ...this.images]
    },
  },
  methods: {
    gotoVehicle(e) {
      this.$store.commit("addRecentVehicle", this.id);
      window.open(e.currentTarget.href, e.currentTarget.target)
    },
    trackBasket(id, name, brand, added) {
      const entry = {
        event: added ? "add_to_cart" : "remove_from_cart",
        items: [{
          id: id,
          name: name,
          list_name: "Favourites List",
          brand: brand,
          category: "Car Subscription",
          list_position: this.$store.state.comparison_list.length + 1,
          quantity: 1,
        }]
      }
      if (added && this.$store.state.user.id) {
        entry.user_id = this.$store.state.user.id
      }
      this.userFavourite = added
      this.$store.commit("setFavouriteCount", added);
      window.dataLayer.push(entry)
    },
    toggleFavourite() {
      if (!this.$store.state.logged_in) {
        return this.$router.push({ name: "login", query: { goto: this.$route.fullPath } });
      }
      if (this.userFavourite) {
        this.$api.delete("api/vehicles/favourites/", { vehicle_ids: [this.id] }).then(r => {
          this.trackBasket(this.id, this.name, this.brand, false)
        })
      } else {
        this.$api.patch("api/vehicles/favourites/", { vehicle_ids: [this.id] }).then(r => {
          this.trackBasket(this.id, this.name, this.brand, true)
        })
      }
    },
    handleSlide(e) {
      const dir = e.currentTarget.dataset.dir == "prev" ? -1 : 1
      const parent = e.currentTarget.parentElement.querySelector('.media-scroller')
      const width = parent.getBoundingClientRect().width
      parent.scrollBy({
        left: width * dir,
        behavior: "smooth"
      })
    },
    renderLargePrice(lprice) {
      let price = parseInt(lprice);
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    }
  },
}
</script>

<style scoped>
a.car-card {
  height: 100%;
  position: relative;
  text-decoration: none;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 100%;
  text-decoration: none;
  color: var(--crf-text);
  border-radius: 8px;
  background-color: var(--crf-bg-high);
  box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;
}

a.car-card.busy {
  pointer-events: none;
}

p.car-badge {
  position: absolute;
  text-transform: uppercase;
  font-size: 0.65rem;
  top: 1rem;
  width: fit-content;
  padding: 2px 5px;
  color: #fff;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: rgba(34, 34, 34, 0.5);
}

.slide-btn {
  border: none;
  position: absolute;
  top: 0;
  width: 30px;
  height: 100%;
  display: grid;
  color: var(--crf-accent);
  place-content: center;
  background: none;
}

.slide-btn[data-dir="next"] {
  right: 0;
}

.slide-btn svg {
  width: 15px;
}

.skeleton {
  position: relative;
}

.busy .skeleton::after {
  content: '';
  inset: 0;
  position: absolute;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  animation: 1s shine linear infinite;
}

.skeleton:hover .slide-btn {
  background-color: rgba(0, 0, 0, 0.025);
}

.media-scroller {
  aspect-ratio: var(--image-aspect);
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 100%;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-snap-type: inline mandatory;
  background: var(--crf-card-gradient);
  padding: 0;
}

.media-scroller>* {
  scroll-snap-align: start;
}

.media-scroller img {
  aspect-ratio: var(--image-aspect);
  height: 100%;
  width: 100%;
  display: block;
  font-size: 0.65rem;
  object-fit: cover;
}

div.content {
  margin: 0;
  padding: 0.75rem 0.85rem;
  display: flex;
  flex-direction: column;
}

div.user-fav svg {
  fill: var(--crf-accent, red);
}

div.meta {
  font-size: 0.8rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

div.meta div.tags span {
  display: inline-block;
  border-radius: 5px;
  padding: 2px 5px;
  margin-right: 3px;
  border: 1px solid var(--crf-border);
}

div.meta div.tags {
  display: flex;
  align-items: center;
}

div.meta div.tags span:first-child {
  max-width: 125px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

p.name {
  margin: 0.75rem 0 1rem 0;
  padding: 0;
  font-family: 'Inter';
  color: var(--crf-text);
  height: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

p.foot {
  text-align: right;
  margin: auto 0 0 0;
  padding: 0;
  color: var(--crf-text);
}

sup.strike {
  color: #DC143C;
  font-family: 'Inter';
  font-size: 0.85rem;
  text-decoration: line-through;
}

span.small-monthly-price {
  display: inline-block;
  font-family: 'Inter';
  color: var(--crf-flip);
  font-size: 0.75em;
}
span.small-monthly-price::after {
  content: ".-"
}
span.small-period {
  font-size: 0.65em;
}

span.price, span.listing-price {
  display: inline-block;
  font-family: 'Inter';
  color: var(--crf-flip);
  font-size: 1.45rem;
}

span.price::after {
  content: ".-"
}

span.period {
  font-size: 0.75rem;
}

@keyframes shine {
  to {
    background-position-x: -200%;
  }
}
</style>
